import { AdCampaignCity } from '@/ui'
import type { BudgetPredictionResults } from '@jobilla/nexus-sdk'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { centsToEuros, eurosToCents } from '../../../constants'
import { Glyphs, Tooltip } from '../../rapid-ui'
import { logger } from '../../utilities/instrumentation/logger'

export type BudgetPredictionProps = {
	prediction: BudgetPredictionResults | null | undefined
	isLoading: boolean
	budgetEuros: number
	onBudgetSelect?: (budgetEuros: number) => void
	locations: AdCampaignCity[]
}

export function BudgetPrediction(
	{ prediction, isLoading, budgetEuros, onBudgetSelect, locations }: BudgetPredictionProps,
) {
	const { t } = useTranslation('editor')

	if (isLoading) {
		return (
			<div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
				<div className="text-sm text-grey-500">{t('budgetPrediction.calculatingEstimate')}</div>
			</div>
		)
	}

	let budgetCents: number | null = null
	let estimatedCandidates: number | null = null
	let recommendedBudgetEuros: number | null = null

	if (prediction?.recommendedBudget && prediction?.recommendedBudget > 0) {
		budgetCents = eurosToCents(budgetEuros)
		estimatedCandidates = Math.floor((budgetCents * 10) / prediction.recommendedBudget)
		recommendedBudgetEuros = Math.round(centsToEuros(prediction.recommendedBudget))
	}

	const handleRecommendedClick = () => {
		if (recommendedBudgetEuros == null) { return }

		logger.track('Editor: Accepted predicted budget', {
			data: {
				predicted_budget: recommendedBudgetEuros,
				current_budget: budgetEuros,
			},
		})

		onBudgetSelect?.(recommendedBudgetEuros!)
	}

	const Container = ({ children }: React.PropsWithChildren) => (
		<div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
			{children}
		</div>
	)

	if (!locations.length) {
		return (
			<Container>
				<p className="text-sm text-grey-500">
					{t('budgetPrediction.chooseLocations')}
				</p>
			</Container>
		)
	}

	if (!recommendedBudgetEuros) {
		return (
			<Container>
				<p className="text-sm text-grey-500">{t('budgetPrediction.cannotPredict')}</p>
			</Container>
		)
	}

	return (
		<Container>
			<div className="flex items-start gap-2">
				<div className="flex-1">
					<div className="text-sm text-grey-700">
						{t('budgetPrediction.predictionText', { budget: budgetEuros })}{' '}
						<span className="font-semibold">
							{estimatedCandidates ?? 0} {(estimatedCandidates ?? 0) === 1
								? t('budgetPrediction.candidate')
								: t('budgetPrediction.candidates')}
						</span>
					</div>
					<div className="mt-2 text-sm text-grey-600">
						{t('budgetPrediction.recommend')}{' '}
						<button
							type="button"
							onClick={handleRecommendedClick}
							className="font-semibold cursor-pointer text-blue-600 hover:text-blue-700 underline"
						>
							€{recommendedBudgetEuros}
						</button>{' '}
						{t('budgetPrediction.toGetCandidates')}
					</div>
				</div>
				<Tooltip
					content={
						<div className="max-w-xs">
							{t('budgetPrediction.seasonalNote')}
						</div>
					}
				>
					<div className="text-blue-600 cursor-help mt-0.5">
						<Glyphs.micro.InfoIcon />
					</div>
				</Tooltip>
			</div>
			<div className="mt-3 pt-3 border-t border-blue-200 text-xs text-grey-600 italic">
				{t('budgetPrediction.disclaimer')}
			</div>
		</Container>
	)
}
